import React, { useState, useEffect, useMemo } from 'react';

export default function SistemaGestionSST({ usuario, onBackToMenu }) {
  const [documentos, setDocumentos] = useState([]);
  const [modalSubir, setModalSubir] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalEliminar, setModalEliminar] = useState(false);
  const [docSeleccionado, setDocSeleccionado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [toast, setToast] = useState({ visible: false, mensaje: '', tipo: '' });
  const [formData, setFormData] = useState({ titulo: '', descripcion: '' });

  const API_URL = process.env.REACT_APP_API_URL;
  const categoriaActual = "Sistema de Gestión";

  // LECTURA DE PERMISOS ABAC
  const permisosABAC = useMemo(() => {
    try {
      if (!usuario || !usuario.permisos_avanzados) return {};
      const parsed = typeof usuario.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) 
        : usuario.permisos_avanzados;
      const micros = parsed.micros || parsed || {};
      return micros[categoriaActual] || {};
    } catch (e) { 
      return {}; 
    }
  }, [usuario]);

  const esSuperAdmin = String(usuario?.cargo || "").toUpperCase().includes("SUPER");
  
  const puedeCrear = esSuperAdmin || Boolean(permisosABAC.crear);
  const puedeEditar = esSuperAdmin || Boolean(permisosABAC.editar);
  const puedeEliminar = esSuperAdmin || Boolean(permisosABAC.eliminar);

  useEffect(() => {
    const cargarDocumentos = async () => {
      try {
        const response = await fetch(`${API_URL}/api/documentos-sst/listar?categoria=${categoriaActual}`);
        if (response.ok) {
          const result = await response.json();
          setDocumentos(result.data || []);
        } else {
          setDocumentos([]);
        }
      } catch (error) {
        console.error("Error al cargar:", error);
      }
    };

    cargarDocumentos();
  }, [API_URL]);

  const mostrarToast = (mensaje, tipo = 'exito') => {
    setToast({ visible: true, mensaje, tipo });
    setTimeout(() => setToast({ visible: false, mensaje: '', tipo: '' }), 4000);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setArchivo(e.target.files[0]);
  };

  const handleSubirSubmit = async (e) => {
    e.preventDefault();
    if (!archivo) return mostrarToast("Debes adjuntar un archivo.", "error");
    setCargando(true);

    try {
      const data = new FormData();
      data.append('categoria', categoriaActual);
      data.append('titulo', formData.titulo);
      data.append('descripcion', formData.descripcion);
      data.append('creado_por', usuario?.nombres || 'Admin');
      data.append('archivo', archivo);

      const response = await fetch(`${API_URL}/api/documentos-sst/crear`, {
        method: 'POST',
        body: data, 
      });
      const result = await response.json();

      if (response.ok && result.success) {
        mostrarToast('Documento subido con éxito');
        setModalSubir(false);
        setFormData({ titulo: '', descripcion: '' });
        setArchivo(null);
        
        // Recargar lista
        const resReload = await fetch(`${API_URL}/api/documentos-sst/listar?categoria=${categoriaActual}`);
        const dataReload = await resReload.json();
        setDocumentos(dataReload.data || []);
      } else {
        mostrarToast('Error al subir documento', 'error');
      }
    } catch (error) {
      mostrarToast('Error de conexión', 'error');
    } finally {
      setCargando(false);
    }
  };

  const abrirModalEditar = (doc) => {
    setDocSeleccionado(doc);
    setFormData({ titulo: doc.titulo, descripcion: doc.descripcion });
    setModalEditar(true);
  };

  const handleEditarSubmit = async (e) => {
    e.preventDefault();
    setCargando(true);
    try {
      const response = await fetch(`${API_URL}/api/documentos-sst/editar/${docSeleccionado.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titulo: formData.titulo, descripcion: formData.descripcion }),
      });
      const result = await response.json();

      if (response.ok && result.success) {
        mostrarToast('Documento actualizado correctamente');
        setModalEditar(false);
        
        const resReload = await fetch(`${API_URL}/api/documentos-sst/listar?categoria=${categoriaActual}`);
        const dataReload = await resReload.json();
        setDocumentos(dataReload.data || []);
      } else {
        mostrarToast('Error al actualizar', 'error');
      }
    } catch (error) {
      mostrarToast('Error de red al actualizar', 'error');
    } finally {
      setCargando(false);
    }
  };

  const confirmarEliminacion = (doc) => {
    setDocSeleccionado(doc);
    setModalEliminar(true);
  };

  const ejecutarEliminacion = async () => {
    setCargando(true);
    try {
      const response = await fetch(`${API_URL}/api/documentos-sst/eliminar/${docSeleccionado.id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (response.ok && result.success) {
        mostrarToast('Documento eliminado');
        setModalEliminar(false);
        
        const resReload = await fetch(`${API_URL}/api/documentos-sst/listar?categoria=${categoriaActual}`);
        const dataReload = await resReload.json();
        setDocumentos(dataReload.data || []);
      } else {
        mostrarToast('Error al eliminar', 'error');
      }
    } catch (error) {
      mostrarToast('Error de red', 'error');
    } finally {
      setCargando(false);
    }
  };

  const getIconoArchivo = (mime) => {
    if (!mime) return "📄";
    if (mime.includes("image")) return "🖼️";
    if (mime.includes("pdf")) return "📕";
    return "📄";
  };

  return (
    <div className="w-full min-h-screen p-4 sm:p-8 animate-fade-in relative z-10">
      
      {/* TOAST FLOTANTE INFERIOR DERECHO */}
      {toast.visible && (
        <div className={`fixed bottom-8 right-8 z-[99999] px-6 py-4 rounded-2xl shadow-2xl font-black text-xs flex items-center gap-3 animate-bounce transition-all ${toast.tipo === 'exito' ? 'bg-emerald-600 text-white shadow-emerald-600/40' : 'bg-rose-600 text-white shadow-rose-600/40'}`}>
          <span>{toast.tipo === 'exito' ? '✅' : '❌'}</span>
          {toast.mensaje}
        </div>
      )}

      <div className="bg-white/60 backdrop-blur-[45px] rounded-[36px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-6 sm:p-10 max-w-7xl mx-auto border border-white/80">
        
        {/* CABECERA */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-200/60 pb-6 mb-8 gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/80 text-slate-700 flex items-center justify-center text-3xl shadow-sm border border-white shrink-0">
              📂
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-800 tracking-tight">Sistema de Gestión SST</h2>
              <p className="text-xs font-bold text-slate-500 mt-1 uppercase tracking-wider">Documentación oficial</p>
            </div>
          </div>
          <div className="flex gap-3">
            {puedeCrear && (
              <button 
                onClick={() => { setFormData({ titulo: '', descripcion: '' }); setModalSubir(true); }}
                className="px-6 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-black tracking-widest transition-all shadow-[0_8px_20px_rgba(37,99,235,0.3)] cursor-pointer"
              >
                + SUBIR DOCUMENTO SST
              </button>
            )}
            <button onClick={onBackToMenu} className="px-6 py-3 rounded-2xl bg-white/70 hover:bg-white text-slate-700 text-xs font-bold transition-all border border-slate-200 shadow-sm cursor-pointer">
              Volver
            </button>
          </div>
        </div>

        {/* LISTADO EN TARJETAS */}
        {documentos.length === 0 ? (
          <div className="text-center py-20 bg-white/30 rounded-[32px] border border-white border-dashed">
            <span className="text-5xl opacity-50 block mb-4">📭</span>
            <p className="text-slate-500 font-bold">No hay archivos registrados en el Sistema de Gestión.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {documentos.map((doc) => (
              <div key={doc.id} className="bg-white/80 backdrop-blur-md rounded-[28px] p-5 border border-white shadow-sm hover:shadow-xl transition-all flex flex-col h-full relative overflow-hidden group">
                <div className="h-32 bg-slate-100/60 rounded-2xl mb-4 flex flex-col items-center justify-center border border-slate-200/50">
                   <span className="text-6xl group-hover:scale-110 transition-transform duration-300">{getIconoArchivo(doc.tipo_mime)}</span>
                </div>
                
                <h3 className="text-sm font-black text-slate-800 leading-tight mb-2 line-clamp-2">{doc.titulo}</h3>
                <p className="text-[11px] text-slate-500 line-clamp-3 mb-4 flex-grow">{doc.descripcion}</p>
                
                <div className="flex justify-between items-center mt-auto pt-4 border-t border-slate-100">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                    {new Date(doc.fecha_creacion).toLocaleDateString()}
                  </span>
                  
                  <div className="flex gap-2 z-10">
                    {puedeEditar && (
                      <button onClick={() => abrirModalEditar(doc)} title="Editar" className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center hover:bg-amber-500 hover:text-white transition-colors cursor-pointer text-sm shadow-sm">
                        ✏️
                      </button>
                    )}
                    {puedeEliminar && (
                      <button onClick={() => confirmarEliminacion(doc)} title="Eliminar" className="w-8 h-8 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-colors cursor-pointer text-sm shadow-sm">
                        🗑️
                      </button>
                    )}
                    <a href={`${API_URL}/api/documentos-sst/archivo/${doc.id}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors cursor-pointer text-sm shadow-sm" title="Ver/Descargar">
                      ⬇️
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL SUBIR */}
      {modalSubir && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-[32px] shadow-2xl p-8 w-full max-w-lg border border-slate-100">
            <h3 className="text-xl font-black text-slate-800 mb-4">Subir Documento SST</h3>
            <form onSubmit={handleSubirSubmit} className="flex flex-col gap-4">
              <input type="text" name="titulo" required placeholder="Título" value={formData.titulo} onChange={handleInputChange} className="bg-slate-50 border rounded-2xl px-4 py-3 text-sm outline-none font-bold text-slate-700" />
              <textarea name="descripcion" required placeholder="Descripción" value={formData.descripcion} onChange={handleInputChange} rows="3" className="bg-slate-50 border rounded-2xl px-4 py-3 text-sm outline-none resize-none font-bold text-slate-700"></textarea>
              <input type="file" required accept=".pdf,image/*" onChange={handleFileChange} className="text-sm text-slate-600 cursor-pointer" />
              <button type="submit" disabled={cargando} className="py-3.5 rounded-2xl bg-blue-600 text-white font-black text-xs uppercase tracking-widest cursor-pointer shadow-lg shadow-blue-500/30">Guardar</button>
              <button type="button" onClick={() => setModalSubir(false)} className="py-2 text-xs font-bold text-slate-500 cursor-pointer">Cancelar</button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL EDITAR */}
      {modalEditar && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-[32px] shadow-2xl p-8 w-full max-w-lg border border-slate-100">
            <h3 className="text-xl font-black text-slate-800 mb-4">Editar Documento SST</h3>
            <form onSubmit={handleEditarSubmit} className="flex flex-col gap-4">
              <input type="text" name="titulo" required value={formData.titulo} onChange={handleInputChange} className="bg-slate-50 border rounded-2xl px-4 py-3 text-sm outline-none font-bold text-slate-700" />
              <textarea name="descripcion" required value={formData.descripcion} onChange={handleInputChange} rows="3" className="bg-slate-50 border rounded-2xl px-4 py-3 text-sm outline-none resize-none font-bold text-slate-700"></textarea>
              <button type="submit" disabled={cargando} className="py-3.5 rounded-2xl bg-amber-500 text-white font-black text-xs uppercase tracking-widest cursor-pointer shadow-lg shadow-amber-500/30">Actualizar</button>
              <button type="button" onClick={() => setModalEditar(false)} className="py-2 text-xs font-bold text-slate-500 cursor-pointer">Cancelar</button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR */}
      {modalEliminar && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-[32px] shadow-2xl p-8 w-full max-w-md text-center">
            <div className="w-16 h-16 bg-rose-100 rounded-full text-rose-500 flex items-center justify-center text-3xl mx-auto mb-4">🗑️</div>
            <h3 className="text-xl font-black text-slate-800 mb-2">¿Eliminar registro?</h3>
            <p className="text-sm text-slate-500 mb-6">Esta acción borrará el archivo permanentemente de la base de datos.</p>
            <div className="flex gap-3">
              <button onClick={() => setModalEliminar(false)} className="flex-1 py-3.5 bg-slate-100 rounded-2xl text-xs font-bold cursor-pointer">Cancelar</button>
              <button onClick={ejecutarEliminacion} disabled={cargando} className="flex-1 py-3.5 bg-rose-600 text-white rounded-2xl text-xs font-black cursor-pointer shadow-lg shadow-rose-600/30">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}